/**
 * printSwatchesDrawer — Alpine.js component
 *
 * Controls the Print Swatches drawer that slides in from the right.
 * Responds to the 'print-swatches:toggle' window event dispatched by
 * the runPrintSwatchesToggle action (arc:action:printSwatchesToggle).
 *
 * Trigger from a button-link block:
 *   Action Preset: Custom
 *   Custom Event IDs: printSwatchesToggle
 */

import { localizeUrl } from '@js/utils/url';

export default {
  name: 'printSwatchesDrawer',
  component(config = {}) {
    return {
      isOpen: false,
      isLoading: false,
      swatchItems: [],
      hasMultipleSwatches: false,
      currentProductHandle: config.currentProductHandle || '',
      customCamillaNamespace: config.customCamillaNamespace || 'custom',
      customCamillaKey: config.customCamillaKey || '',
      customCamillaSourceValue: config.customCamillaSourceValue || null,
      maxSwatches: Number(config.maxSwatches) || 20,

      init() {
        this.setTriggerVisibility(false);
        this.loadSwatches();

        this._toggleHandler = () => this.toggle();
        this._openHandler   = () => this.open();
        this._closeHandler  = () => this.close();

        window.addEventListener('print-swatches:toggle', this._toggleHandler);
        window.addEventListener('print-swatches:open',   this._openHandler);
        window.addEventListener('print-swatches:close',  this._closeHandler);

        this._keyHandler = (e) => {
          if (e.key === 'Escape' && this.isOpen) this.close();
        };
        document.addEventListener('keydown', this._keyHandler);
      },

      destroy() {
        window.removeEventListener('print-swatches:toggle', this._toggleHandler);
        window.removeEventListener('print-swatches:open',   this._openHandler);
        window.removeEventListener('print-swatches:close',  this._closeHandler);
        document.removeEventListener('keydown', this._keyHandler);
        document.body.classList.remove('no-scroll');
        this.setTriggerVisibility(true);
      },

      setTriggerVisibility(shouldShow) {
        const triggerButton = document.getElementById('product__swatch-drawer');
        if (!triggerButton) return;

        if (shouldShow) {
          triggerButton.style.removeProperty('display');
          triggerButton.removeAttribute('hidden');
          triggerButton.setAttribute('aria-hidden', 'false');
          return;
        }

        triggerButton.style.setProperty('display', 'none', 'important');
        triggerButton.setAttribute('hidden', 'hidden');
        triggerButton.setAttribute('aria-hidden', 'true');
      },

      parseSourceValue(value) {
        if (typeof value !== 'string') return value;
        if (!value.length) return value;

        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      },

      normaliseSourceValue(value) {
        const parsedValue = this.parseSourceValue(value);

        if (Array.isArray(parsedValue)) {
          return this.normaliseSourceValue(parsedValue[0]);
        }

        if (parsedValue && typeof parsedValue === 'object') {
          if (typeof parsedValue.value === 'string') return parsedValue.value;
          if (typeof parsedValue.handle === 'string') return parsedValue.handle;
          if (typeof parsedValue.title === 'string') return parsedValue.title;
          return '';
        }

        if (parsedValue === undefined || parsedValue === null) return '';
        return String(parsedValue).trim();
      },

      escapeSearchValue(value) {
        return String(value)
          .replace(/\\/g, '\\\\')
          .replace(/'/g, "\\'")
          .replace(/"/g, '\\"');
      },

      setSwatches(items) {
        this.swatchItems = Array.isArray(items) ? items : [];
        this.hasMultipleSwatches = this.swatchItems.length > 1;
        this.setTriggerVisibility(this.hasMultipleSwatches);

        if (!this.hasMultipleSwatches) {
          this.close();
        }
      },

      async loadSwatches() {
        if (!this.customCamillaKey) {
          this.setSwatches([]);
          return;
        }

        let sourceValue = this.normaliseSourceValue(this.customCamillaSourceValue);

        const accessToken = window.themeSettings?.storefront_access_token;
        if (!accessToken) {
          this.setSwatches([]);
          return;
        }

        if (this.currentProductHandle) {
          const fetchInitialQuery = `
            query ($handle: String!, $namespace: String!, $key: String!) {
              product(handle: $handle) {
                metafield(namespace: $namespace, key: $key) {
                  value
                }
              }
            }
          `;
          const fetchInitialVariables = {
            handle: this.currentProductHandle,
            namespace: this.customCamillaNamespace,
            key: this.customCamillaKey
          };
          try {
            const initRes = await fetch('/api/2023-07/graphql.json', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': accessToken,
                'Accept-Language': 'en'
              },
              body: JSON.stringify({ query: fetchInitialQuery, variables: fetchInitialVariables })
            });
            if (initRes.ok) {
              const initPayload = await initRes.json();
              if (initPayload?.data?.product?.metafield?.value) {
                sourceValue = this.normaliseSourceValue(initPayload.data.product.metafield.value);
              }
            }
          } catch (e) {
            console.warn('[print-swatches-drawer] Failed to fetch initial untranslated metafield', e);
          }
        }

        if (!sourceValue) {
          this.setSwatches([]);
          return;
        }

        this.isLoading = true;

        const queryString = `metafield:${this.customCamillaNamespace}.${this.customCamillaKey}:'${this.escapeSearchValue(sourceValue)}'`;

        const query = `
          query GetProductsByGlobalTitleType($query: String!) {
            products(first: 100, query: $query) {
              edges {
                node {
                  id
                  title
                  handle
                  availableForSale
                  onlineStoreUrl
                  tags
                  images(first: 10) {
                    edges {
                      node {
                        id
                        url: url(transform: {maxWidth: 332, maxHeight: 496})
                        altText
                      }
                    }
                  }
                  silhouette: metafield(namespace: "custom", key: "product_silhouette") {
                    value
                  }
                  customTitle: metafield(namespace: "global", key: "title_type") {
                    value
                  }
                  printCollection: metafield(namespace: "custom", key: "print_collection") {
                    reference {
                      ... on Metaobject {
                        field(key: "circular_image") {
                          reference {
                            ... on MediaImage {
                              image {
                                url
                                altText
                              }
                            }
                          }
                        }
                        featuredImage: field(key: "featured_image") {
                          reference {
                            ... on MediaImage {
                              image {
                                url
                                altText
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `;

        try {
          const locale = window.Shopify?.locale || document.documentElement.lang || 'en';
          const response = await fetch('/api/2023-07/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': accessToken,
              'Accept-Language': locale
            },
            body: JSON.stringify({ query, variables: { query: queryString } })
          });

          if (!response.ok) {
            this.setSwatches([]);
            return;
          }

          const payload = await response.json();
          const edges = payload?.data?.products?.edges || [];
          const nodes = edges.map((edge) => edge?.node).filter(Boolean);

          const currentProduct = nodes.find((node) => node?.handle === this.currentProductHandle);
          const currentSilhouetteValue = String(currentProduct?.silhouette?.value || '').trim();
          const currentProductTags = currentProduct?.tags || [];
          const currentBadgeTags = currentProductTags.filter((tag) => tag.startsWith('Badge:'));

          let filteredNodes = currentSilhouetteValue
            ? nodes.filter((node) => String(node?.silhouette?.value || '').trim() === currentSilhouetteValue)
            : nodes;

          if (currentBadgeTags.length > 0) {
            filteredNodes = filteredNodes.filter((node) => {
              const nodeTags = node?.tags || [];
              return currentBadgeTags.some((badgeTag) => nodeTags.includes(badgeTag));
            });
          }

          const items = filteredNodes
            .map((node) => {
              const circularImage = node?.printCollection?.reference?.field?.reference?.image;
              const featuredImage = node?.printCollection?.reference?.featuredImage?.reference?.image;
              const fallbackProductImage = node?.images?.edges?.[2]?.node;
              const productTitle = node?.title.replace(/\w\S*/g, t => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase());
              return {
                handle: node?.handle || '',
                label:  productTitle || '',
                url: node?.onlineStoreUrl || (node?.handle ? localizeUrl(`/products/${node.handle}`) : '#'),
                imageUrl: fallbackProductImage?.url ||  circularImage?.url || featuredImage?.url ||'',
                isSelected: node?.handle === this.currentProductHandle
              };
            })
            .filter((item) => item.handle && item.label)
            .slice(0, this.maxSwatches);

          this.setSwatches(items);
        } catch (error) {
          console.error('[print-swatches-drawer] Custom Camilla Logic fetch failed', error);
          this.setSwatches([]);
        } finally {
          this.isLoading = false;
        }
      },

      open() {
        if (!this.hasMultipleSwatches) return;
        this.isOpen = true;
      },

      close() {
        this.isOpen = false;
      },

      toggle() {
        if (!this.hasMultipleSwatches) return;
        this.isOpen ? this.close() : this.open();
      },
    };
  },
};
